"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Section, Typography } from "@/components/ui";

interface GettingStartedStep {
  id: number;
  title: string;
  description?: string;
  image: string;
  alt: string;
}

interface GettingStartedCarouselProps {
  title: string;
  steps: GettingStartedStep[];
  className?: string;
}

export default function GettingStartedCarousel({
  title,
  steps,
  className = "",
}: GettingStartedCarouselProps) {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (steps.length <= 1) return;

    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % steps.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [steps.length]);

  // Guard against empty steps array
  const currentStep = steps[activeStep] ?? steps[0] ?? null;

  return (
    <Section variant="default" background="gray" className={className}>
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="section-label">Getting Started</span>
          <Typography as="h2" variant="h2">
            {title}
          </Typography>
          <Typography as="p" variant="body" className="text-text-secondary leading-relaxed mt-4">
            Follow these simple steps to set up Pickcel Go and start managing screens from your
            mobile device.
          </Typography>
        </div>

        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
            {steps.map((step, index) => (
              <button
                key={step.id}
                type="button"
                onClick={() => setActiveStep(index)}
                aria-label={`Step ${index + 1}: ${step.title}`}
                className={`group w-full rounded-2xl border p-5 text-left transition-all duration-200 ${
                  index === activeStep
                    ? "border-primary bg-white shadow-sm"
                    : "border-gray-200 bg-white/70 hover:border-primary/40 hover:bg-white"
                }`}
              >
                <div className="flex justify-center items-center gap-4">
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                      index === activeStep
                        ? "bg-primary text-white"
                        : "bg-primary/8 text-primary group-hover:bg-primary/12"
                    }`}
                  >
                    {step.id}
                  </div>
                  <div className="flex-1">
                    <Typography
                      as="h3"
                      variant="h4"
                      className={`font-semibold ${
                        index === activeStep ? "text-text-primary" : "text-gray-500"
                      }`}
                    >
                      {step.title}
                    </Typography>
                    {step.description && (
                      <Typography
                        as="p"
                        variant="small"
                        className="mt-2 text-text-secondary"
                      >
                        {step.description}
                      </Typography>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="flex justify-center">
            {currentStep ? (
              <div className="w-full max-w-5xl rounded-[32px] border border-primary/10 bg-white p-4 md:p-6 shadow-[0_18px_48px_rgba(15,23,42,0.08)]">
                <div className="overflow-hidden rounded-[24px] bg-linear-to-b from-slate-50 to-white flex justify-center">
                  <Image
                    src={currentStep.image}
                    alt={currentStep.alt ?? ""}
                    width={600}
                    height={400}
                    loading="lazy"
                    sizes="(max-width: 768px) 100vw, 900px"
                    className="w-auto h-auto max-w-full"
                  />
                </div>
              </div>
            ) : (
              <div className="w-full h-[500px] bg-gray-100 rounded-lg flex items-center justify-center">
                <Typography variant="body" className="text-gray-400">
                  No image available
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}
